export function mapDeepLinkToRoute(link: string, options?: { showGroups?: boolean; residentsCanCreateEvents?: boolean; showEvents?: boolean; showPlaces?: boolean }): string | null {
  if (!link) return null;

  const showGroups = options?.showGroups ?? true;
  const residentsCanCreateEvents = options?.residentsCanCreateEvents ?? true;
  const showEvents = options?.showEvents ?? true;
  // Defaults to false, unlike the flags above: Places is opt-in per building,
  // and its routes aren't mounted when it's off. A caller that forgets to pass
  // the flag should land on home rather than on an unmounted route (blank screen).
  const showPlaces = options?.showPlaces ?? false;

  // Normalize: trim, strip scheme and leading slashes
  let normalized = link.trim();

  // Handle new URL format: https://[example].com/cobu/[DL]
  if (normalized.includes('/cobu/')) {
    const cobuIndex = normalized.indexOf('/cobu/');
    normalized = normalized.slice(cobuIndex + '/cobu/'.length);
  }
  // Strip old cobu:// if present
  else if (normalized.startsWith('cobu://')) {
    normalized = normalized.slice('cobu://'.length);
  }

  // Also handle bare paths that might start with a slash
  normalized = normalized.replace(/^\/+/, '');

  if (!normalized) return null;

  const segments = normalized.split('/');
  const [root, ...rest] = segments;

  // --- Groups ---
  if (root === 'groups') {
    const [sub, ...subRest] = rest;

    // cobu://groups/gatheringDetails/${groupEventId}/0
    // cobu://groups/gatheringDetails/${groupEventId}/${commentId}
    // A gathering is an EVENT, not a group. The backend nests it under the
    // `groups/` root, but it must be gated by showEvents (not showGroups) so
    // event deep links still resolve when the Groups tab is disabled.
    if (sub === 'gatheringDetails') {
      if (!showEvents) return '/';
      const eventId = subRest[0];
      const commentId = subRest[1];
      if (eventId) {
        if (commentId && commentId !== '0') {
          const qs = new URLSearchParams({
            commentId,
            scrollToComment: 'true',
          });
          return `/events/${eventId}?${qs.toString()}`;
        }
        return `/events/${eventId}`;
      }
      return '/events';
    }

    if (!showGroups) return '/';

    // cobu://groups
    if (rest.length === 0) {
      return '/groups';
    }

    // cobu://groups/groupDetails/${groupId}/0
    // cobu://groups/groupDetails/${groupId}/${commentId}
    if (sub === 'groupDetails') {
      const groupId = subRest[0];
      const commentId = subRest[1];
      if (groupId) {
        if (commentId && commentId !== '0') {
          const qs = new URLSearchParams({
            commentId,
            scrollToComment: 'true',
          });
          return `/groups/${groupId}?${qs.toString()}`;
        }
        return `/groups/${groupId}`;
      }
      return '/groups';
    }

    return '/groups';
  }

  // --- Places ---
  if (root === 'places') {
    if (!showPlaces) return '/';

    // cobu://places
    if (rest.length === 0) {
      return '/places';
    }

    const [sub, ...subRest] = rest;

    // cobu://places/placeDetails/${placeId} — mirrors the `xDetails` shape the
    // groups and events links use, in case the backend ever sends that form.
    if (sub === 'placeDetails') {
      const placeId = subRest[0];
      return placeId ? `/places/${placeId}` : '/places';
    }

    // cobu://places/${placeId} — the flat shape the place notifications send.
    // Any trailing segment is dropped: the detail screen has no comment
    // anchoring, so there's no commentId query to build here.
    return `/places/${sub}`;
  }

  // --- Perks ---
  if (root === 'perks') {
    if (rest.length === 0) {
      // cobu://perks
      return '/perks';
    }

    const [sub, ...subRest] = rest;

    // cobu://perks/referralDetails/${buildingId}
    if (sub === 'referralDetails') {
      // Web route doesn't currently take buildingId in the path; it uses /perks/referral
      return '/perks/referral';
    }

    // cobu://perks/perkDetails/${perkId}
    if (sub === 'perkDetails') {
      const perkId = subRest[0];
      if (perkId) {
        return `/perks/${perkId}`;
      }
      return '/perks';
    }

    return '/perks';
  }

  // --- Chat ---
  if (root === 'chat') {
    if (rest.length === 0) {
      return '/chat';
    }

    const [sub, ...subRest] = rest;

    // cobu://chat/dm/${userId}  -> intermediate DM route
    if (sub === 'dm') {
      const userId = subRest[0];
      if (userId) {
        return `/chat/dm/${userId}`;
      }
      return '/chat';
    }

    // cobu://chat/discussion/${commentId} -> building-wide chat
    if (sub === 'discussion') {
      const commentId = subRest[0];
      if (commentId) {
        const qs = new URLSearchParams({
          commentId,
          scrollToComment: 'true',
        });
        return `/chat?${qs.toString()}`;
      }
      return '/chat';
    }

    // Fallback: go to chat tab
    return '/chat';
  }

  // --- Marketplace ---
  if (root === 'marketplace') {
    // cobu://marketplace/groupDetails/${groupId}/${commentId}
    // We don't currently have per-group marketplace routes on web,
    // everything is under the main marketplace screen.
    return '/marketplace';
  }

  // --- Events ---
  if (root === 'events') {
    if (!showEvents) return '/chat';

    if (rest.length === 0) {
      return '/events';
    }

    const [sub, ...subRest] = rest;

    // cobu://events/eventDetails/${eventId}/${commentId?}
    if (sub === 'eventDetails') {
      const eventId = subRest[0];
      const commentId = subRest[1];
      if (eventId) {
        if (commentId && commentId !== '0') {
          const qs = new URLSearchParams({
            commentId,
            scrollToComment: 'true',
          });
          return `/events/${eventId}?${qs.toString()}`;
        }
        return `/events/${eventId}`;
      }
      return '/events';
    }

    // cobu://events/groupDetails/${groupId}/${commentId?}
    if (sub === 'groupDetails') {
      const groupId = subRest[0];
      const commentId = subRest[1];
      if (groupId) {
        if (commentId && commentId !== '0') {
          const qs = new URLSearchParams({
            commentId,
            scrollToComment: 'true',
          });
          return `/groups/${groupId}?${qs.toString()}`;
        }
        return `/groups/${groupId}`;
      }
      return '/groups';
    }

    // cobu://events/gatheringDetails/${eventId}/${commentId?}
    if (sub === 'gatheringDetails') {
      const eventId = subRest[0];
      const commentId = subRest[1];
      if (eventId) {
        if (commentId && commentId !== '0') {
          const qs = new URLSearchParams({
            commentId,
            scrollToComment: 'true',
          });
          return `/events/${eventId}?${qs.toString()}`;
        }
        return `/events/${eventId}`;
      }
      return '/events';
    }

    return '/events';
  }

  // Unknown root: no mapping
  return null;
}


